import type { SortingState } from '@tanstack/react-table';

import type { getSearchResponseResponseSuccess } from '#/api/generated/search/search';
import type {
  SearchExchangeCode,
  SearchMarketCapCategory,
} from '#/components/search/facetOptions.ts';
import type { SearchListingType } from '#/components/search/searchFacetFilters.types';

export type SearchInvestmentType = 'equity' | 'etf' | 'mutual_fund' | undefined;

export interface SearchStateInput {
  query: string;
  investment_type: SearchInvestmentType;
  sort_by?: string;
  order_by?: 'asc' | 'desc';
  equity_sector_slug?: string[];
  equity_industry_slug?: string[];
  market_cap_categories?: SearchMarketCapCategory[];
  fund_category_slug?: string[];
  listing_type?: SearchListingType;
  exchange?: SearchExchangeCode[];
  equity_next_pagination_token?: string;
  equity_prev_pagination_token?: string;
  etf_next_pagination_token?: string;
  etf_prev_pagination_token?: string;
  mutual_fund_next_pagination_token?: string;
  mutual_fund_prev_pagination_token?: string;
}

export type SearchPaginationDirection = 'next' | 'prev';

export type SearchPreviewState = Omit<
  Partial<SearchStateInput>,
  'query' | 'investment_type'
> & {
  investment_type?: SearchInvestmentType;
};

export type SearchSortBy =
  | 'expense_ratio'
  | 'fund_size'
  | 'market_cap'
  | 'price'
  | 'return_1y'
  | 'return_5y';

export type SearchOrderBy = 'asc' | 'desc';

const EQUITY_SORT_COLUMNS = {
  market_cap: 'market_cap',
  price: 'price',
  return_1y: 'trailing_1yr_return',
  return_5y: 'trailing_5yr_return',
} as const;

const ETF_SORT_COLUMNS = {
  expense_ratio: 'expense_ratio',
  price: 'price',
  return_1y: 'trailing_1yr_return',
  return_5y: 'return_5Y_annualized',
} as const;

const MUTUAL_FUND_SORT_COLUMNS = {
  expense_ratio: 'expense_ratio',
  fund_size: 'fund_size',
  price: 'price',
  return_1y: 'trailing_1yr_return',
  return_5y: 'return_5Y_annualized',
} as const;

export function toSearchInvestmentType(
  value: string | undefined,
): SearchInvestmentType {
  if (value === 'equity' || value === 'etf' || value === 'mutual_fund') {
    return value;
  }

  return undefined;
}

export function toSearchOrderBy(
  value: string | undefined,
): SearchOrderBy | undefined {
  if (value === 'asc' || value === 'desc') {
    return value;
  }

  return undefined;
}

export function getSearchSortBy(
  investmentType: SearchInvestmentType,
  value: string | undefined,
): SearchSortBy | undefined {
  if (!investmentType || !value) {
    return undefined;
  }

  if (investmentType === 'equity') {
    return value in EQUITY_SORT_COLUMNS ? (value as SearchSortBy) : undefined;
  }

  if (investmentType === 'etf') {
    return value in ETF_SORT_COLUMNS ? (value as SearchSortBy) : undefined;
  }

  return value in MUTUAL_FUND_SORT_COLUMNS
    ? (value as SearchSortBy)
    : undefined;
}

export function getSearchSorting(
  investmentType: SearchInvestmentType,
  sortBy: string | undefined,
  orderBy: string | undefined,
): SortingState {
  const normalizedSortBy = getSearchSortBy(investmentType, sortBy);

  if (!investmentType || !normalizedSortBy) {
    return [];
  }

  if (investmentType === 'equity') {
    return [
      {
        id: EQUITY_SORT_COLUMNS[
          normalizedSortBy as keyof typeof EQUITY_SORT_COLUMNS
        ],
        desc: toSearchOrderBy(orderBy) !== 'asc',
      },
    ];
  }

  if (investmentType === 'etf') {
    return [
      {
        id: ETF_SORT_COLUMNS[normalizedSortBy as keyof typeof ETF_SORT_COLUMNS],
        desc: toSearchOrderBy(orderBy) !== 'asc',
      },
    ];
  }

  return [
    {
      id: MUTUAL_FUND_SORT_COLUMNS[
        normalizedSortBy as keyof typeof MUTUAL_FUND_SORT_COLUMNS
      ],
      desc: toSearchOrderBy(orderBy) !== 'asc',
    },
  ];
}

export function getSearchSortParams(
  investmentType: SearchInvestmentType,
  sorting: SortingState,
): {
  sort_by?: SearchSortBy;
  order_by?: SearchOrderBy;
} {
  if (!investmentType) {
    return {};
  }

  const activeColumnId = sorting[0]?.id;

  if (!activeColumnId) {
    return {};
  }

  const sortColumns =
    investmentType === 'equity'
      ? EQUITY_SORT_COLUMNS
      : investmentType === 'etf'
        ? ETF_SORT_COLUMNS
        : MUTUAL_FUND_SORT_COLUMNS;

  const sortBy = Object.entries(sortColumns).find(
    ([, columnId]) => columnId === activeColumnId,
  )?.[0] as SearchSortBy | undefined;

  if (!sortBy) {
    return {};
  }

  return {
    sort_by: sortBy,
    order_by: sorting[0]?.desc === false ? 'asc' : 'desc',
  };
}

export function getPaginationParams(
  investmentType: SearchInvestmentType,
  direction: SearchPaginationDirection,
  cursor?: string,
) {
  if (!cursor) {
    return {};
  }

  if (investmentType === 'equity') {
    return direction === 'next'
      ? { equity_next_pagination_token: cursor }
      : { equity_prev_pagination_token: cursor };
  }

  if (investmentType === 'etf') {
    return direction === 'next'
      ? { etf_next_pagination_token: cursor }
      : { etf_prev_pagination_token: cursor };
  }

  if (investmentType === 'mutual_fund') {
    return direction === 'next'
      ? { mutual_fund_next_pagination_token: cursor }
      : { mutual_fund_prev_pagination_token: cursor };
  }

  return {};
}

export function getPaginationTokens(
  investmentType: SearchInvestmentType,
  response: getSearchResponseResponseSuccess,
) {
  if (investmentType === 'equity') {
    return {
      next: response.data.equity?.next_pagination_token ?? undefined,
      prev: response.data.equity?.prev_pagination_token ?? undefined,
    };
  }

  if (investmentType === 'etf') {
    return {
      next: response.data.etf?.next_pagination_token ?? undefined,
      prev: response.data.etf?.prev_pagination_token ?? undefined,
    };
  }

  if (investmentType === 'mutual_fund') {
    return {
      next: response.data.mutual_fund?.next_pagination_token ?? undefined,
      prev: response.data.mutual_fund?.prev_pagination_token ?? undefined,
    };
  }

  return {
    next: undefined,
    prev: undefined,
  };
}

export function buildSearchState({
  query,
  investment_type,
  sort_by,
  order_by,
  equity_sector_slug,
  equity_industry_slug,
  market_cap_categories,
  fund_category_slug,
  listing_type,
  exchange,
  equity_next_pagination_token,
  equity_prev_pagination_token,
  etf_next_pagination_token,
  etf_prev_pagination_token,
  mutual_fund_next_pagination_token,
  mutual_fund_prev_pagination_token,
}: SearchStateInput) {
  return {
    query,
    investment_type,
    sort_by: investment_type
      ? getSearchSortBy(investment_type, sort_by)
      : undefined,
    order_by:
      investment_type && getSearchSortBy(investment_type, sort_by)
        ? order_by
        : undefined,
    equity_sector_slug:
      investment_type === 'equity' ? equity_sector_slug : undefined,
    equity_industry_slug:
      investment_type === 'equity' ? equity_industry_slug : undefined,
    market_cap_categories:
      investment_type === 'equity' ? market_cap_categories : undefined,
    fund_category_slug:
      investment_type === 'etf' || investment_type === 'mutual_fund'
        ? fund_category_slug
        : undefined,
    listing_type:
      investment_type === 'equity' || investment_type === 'etf'
        ? exchange?.length
          ? undefined
          : listing_type
        : undefined,
    exchange:
      investment_type === 'equity' || investment_type === 'etf'
        ? exchange
        : undefined,
    equity_next_pagination_token:
      investment_type === 'equity' ? equity_next_pagination_token : undefined,
    equity_prev_pagination_token:
      investment_type === 'equity' ? equity_prev_pagination_token : undefined,
    etf_next_pagination_token:
      investment_type === 'etf' ? etf_next_pagination_token : undefined,
    etf_prev_pagination_token:
      investment_type === 'etf' ? etf_prev_pagination_token : undefined,
    mutual_fund_next_pagination_token:
      investment_type === 'mutual_fund'
        ? mutual_fund_next_pagination_token
        : undefined,
    mutual_fund_prev_pagination_token:
      investment_type === 'mutual_fund'
        ? mutual_fund_prev_pagination_token
        : undefined,
  };
}
